import { Injectable, signal } from '@angular/core';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

export interface NotificationAction {
  label: string;
  run: () => void;
}

export interface AppNotification {
  id: number;
  message: string;
  type: NotificationType;
  durationMs: number;
  // Botón opcional dentro del aviso (p. ej. "Ver planes" cuando una función
  // no está incluida en el plan del negocio).
  action?: NotificationAction;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  readonly items = signal<AppNotification[]>([]);
  private nextId = 1;
  private readonly recent = new Map<string, number>();

  show(message: string, type: NotificationType = 'info', duration = 4500, actionKey?: string, action?: NotificationAction): void {
    const clean = String(message || '').trim();
    if (!clean) return;
    const fingerprint = `${actionKey || clean.toLocaleLowerCase().replace(/\s+/g, ' ')}:${type}`;
    const now = Date.now();
    if ((this.recent.get(fingerprint) || 0) > now - 5000) return;
    this.recent.set(fingerprint, now);
    for (const [key, timestamp] of this.recent) if (timestamp < now - 15000) this.recent.delete(key);
    // Evitar duplicados inmediatos: si el último mensaje es idéntico y del mismo tipo, no lo mostramos de nuevo
    const current = this.items();
    const last = current.length ? current[current.length - 1] : null;
    if (last && last.message === clean && last.type === type) return;
    const item: AppNotification = { id: this.nextId++, message: clean, type, durationMs: duration, ...(action ? { action } : {}) };
    this.items.update(items => [...items.filter(existing => existing.message !== clean || existing.type !== type).slice(-2), item]);
    if (duration > 0) setTimeout(() => this.dismiss(item.id), duration);
  }

  success(message: string): void { this.show(message, 'success'); }
  error(message: string): void { this.show(message, 'error', 6500); }
  warning(message: string): void { this.show(message, 'warning', 6000); }
  dismiss(id: number): void { this.items.update(items => items.filter(item => item.id !== id)); }
}
